import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import SuiBox from "components/SuiBox";
import Box from "@mui/material/Box";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import { Divider } from "@mui/material";
import ProcesosApi from "api/procesosApi";
import AdicionalCard from "./components/AdicionalCard";
import EditAdicionalModal from "./components/EditAdicionalModal";
import NuevoAdicionalForm from "./components/NuevoAdicionalForm";

const TIPOS_ADICIONALES = ["PERSONA_CARGO", "SOCIEDADES_PROCESO", "PROCESO_JUDICIAL"];

const AdicionalesManager = ({
  procesoId,
  showNewAdicionalModal,
  setShowNewAdicionalModal,
  handleNext = () => {}, // Parámetro por defecto aquí
}) => {
  const [adicionales, setAdicionales] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAdicional, setSelectedAdicional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [procesoData, setProcesoData] = useState(null);

  const verificarProceso = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener datos de la proceso específica
      const procesoResponse = await ProcesosApi.fetchProcesoById(procesoId);
      const procesoActual = procesoResponse.data;

      if (!procesoActual) {
        setError("No se encontró la proceso");
        return;
      }

      setProcesoData(procesoActual);

      // Verificar si al menos uno de los campos adicionales está en true
      const poseeAdicionales =
        procesoActual.procesos_judiciales ||
        procesoActual.personas_a_cargo ||
        procesoActual.sociedades;

      if (!poseeAdicionales) {
        if (handleNext) {
          handleNext();
        }
        return;
      }

      // Obtener todos los tipos de adicionales si están habilitados
      const adicionalesFormateados = [];

      // Obtener personas a cargo si están habilitadas
      if (procesoActual.personas_a_cargo) {
        const personasResponse = await ProcesosApi.obtenerPersonasCargo(procesoId);
        adicionalesFormateados.push(
          ...personasResponse.data.map((p) => ({ ...p, tipo_adicional: "PERSONA_CARGO" }))
        );
      }

      // Obtener sociedades si están habilitadas
      if (procesoActual.sociedades) {
        const sociedadesResponse = await ProcesosApi.obtenerSociedades(procesoId);
        adicionalesFormateados.push(
          ...sociedadesResponse.data.map((s) => ({ ...s, tipo_adicional: "SOCIEDADES_PROCESO" }))
        );
      }

      // Obtener procesos juridicos si están habilitados
      if (procesoActual.procesos_judiciales) {
        const procesosResponse = await ProcesosApi.obtenerProcesosJudiciales(procesoId);
        adicionalesFormateados.push(
          ...procesosResponse.data.map((p) => ({ ...p, tipo_adicional: "PROCESO_JUDICIAL" }))
        );
      }

      setAdicionales(adicionalesFormateados);
    } catch (error) {
      console.error("Error al cargar los datos de Adicionales del Proceso:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos de Adicionales del Proceso"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (procesoId) {
      verificarProceso();
    }
  }, [procesoId]);

  const handleDelete = async (adicional) => {
    if (!window.confirm("¿Está seguro(a) de eliminar este adicional?")) return;
    try {
      setError(null);
      switch (adicional.tipo_adicional) {
        case "PERSONA_CARGO":
          await ProcesosApi.eliminarPersonaCargo(procesoId, adicional.id);
          break;
        case "SOCIEDADES_PROCESO":
          await ProcesosApi.eliminarSociedad(procesoId, adicional.id);
          break;
        case "PROCESO_JUDICIAL":
          await ProcesosApi.eliminarProcesoJuridicos(procesoId, adicional.id);
          break;
        default:
          throw new Error("Tipo de adicional no válido");
      }
      // Recargar todos los tipos de adicionales
      const adicionalesFormateados = [];
      if (procesoData.personas_a_cargo) {
        const personasResponse = await ProcesosApi.obtenerPersonasCargo(procesoId);
        adicionalesFormateados.push(
          ...personasResponse.data.map((p) => ({ ...p, tipo_adicional: "PERSONA_CARGO" }))
        );
      }
      if (procesoData.sociedades) {
        const sociedadesResponse = await ProcesosApi.obtenerSociedades(procesoId);
        adicionalesFormateados.push(
          ...sociedadesResponse.data.map((s) => ({ ...s, tipo_adicional: "SOCIEDADES_PROCESO" }))
        );
      }
      if (procesoData.procesos_judiciales) {
        const procesosResponse = await ProcesosApi.obtenerProcesosJudiciales(procesoId);
        adicionalesFormateados.push(
          ...procesosResponse.data.map((p) => ({ ...p, tipo_adicional: "PROCESO_JUDICIAL" }))
        );
      }
      setAdicionales(adicionalesFormateados);
    } catch (error) {
      console.error("Error al eliminar Adicional:", error);
      setError(obtenerMensajeError(error, "Error al eliminar el Adicional"));
    }
  };

  const handleEdit = (adicional) => {
    setSelectedAdicional(adicional);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (adicionalActualizado) => {
    try {
      setError(null);
      switch (adicionalActualizado.tipo_adicional) {
        case "PERSONA_CARGO":
          await ProcesosApi.actualizarPersonaCargo(
            procesoId,
            adicionalActualizado.id,
            adicionalActualizado
          );
          break;
        case "SOCIEDADES_PROCESO":
          await ProcesosApi.actualizarSociedad(
            procesoId,
            adicionalActualizado.id,
            adicionalActualizado
          );
          break;
        case "PROCESO_JUDICIAL":
          await ProcesosApi.actualizarProcesoJudicial(
            procesoId,
            adicionalActualizado.id,
            adicionalActualizado
          );
          break;
        default:
          throw new Error("Tipo Adicional no válido");
      }
      // Actualizar el estado local
      // setAdicionales((prevAdicionales) =>
      //   prevAdicionales.map((adicional) =>
      //     adicional.id === adicionalActualizado.id
      //       ? { ...response.data, tipo_adicional: adicionalActualizado.tipo_adicional }
      //       : adicional
      //   )
      // );

      // Recargar todos los tipos de adicionales
      const adicionalesFormateados = [];
      if (procesoData.personas_a_cargo) {
        const personasResponse = await ProcesosApi.obtenerPersonasCargo(procesoId);
        adicionalesFormateados.push(
          ...personasResponse.data.map((p) => ({ ...p, tipo_adicional: "PERSONA_CARGO" }))
        );
      }
      if (procesoData.sociedades) {
        const sociedadesResponse = await ProcesosApi.obtenerSociedades(procesoId);
        adicionalesFormateados.push(
          ...sociedadesResponse.data.map((s) => ({ ...s, tipo_adicional: "SOCIEDADES_PROCESO" }))
        );
      }
      if (procesoData.procesos_judiciales) {
        const procesosResponse = await ProcesosApi.obtenerProcesosJudiciales(procesoId);
        adicionalesFormateados.push(
          ...procesosResponse.data.map((p) => ({ ...p, tipo_adicional: "PROCESO_JUDICIAL" }))
        );
      }
      setAdicionales(adicionalesFormateados);
      setEditModalOpen(false);
      setSelectedAdicional(null);
    } catch (error) {
      console.error("Error al actualizar el Adicional:", error);
      setError(obtenerMensajeError(error, "Error al actualizar el Adicional"));
    }
  };

  const handleNewAdicionalSubmit = async (adicionalData) => {
    try {
      setError(null);
      let response;

      switch (adicionalData.tipo_adicional) {
        case "PERSONA_CARGO":
          response = await ProcesosApi.crearPersonaCargo(procesoId, adicionalData);
          break;
        case "SOCIEDADES_PROCESO":
          response = await ProcesosApi.crearSociedad(procesoId, adicionalData);
          break;
        case "PROCESO_JUDICIAL":
          response = await ProcesosApi.crearProcesoJudicial(procesoId, adicionalData);
          break;
        default:
          throw new Error("Tipo de adicional no válido");
      }

      // Agregar solo el nuevo adicional al estado actual
      setAdicionales((prevAdicionales) => [
        ...prevAdicionales,
        { ...response.data, tipo_adicional: adicionalData.tipo_adicional },
      ]);

      setShowNewAdicionalModal(false);
    } catch (error) {
      console.error("Error al crear adicional:", error);
      setError(obtenerMensajeError(error, "Error al crear el adicional"));
    }
  };

  const adicionalesPorTipo = useMemo(() => {
    return adicionales.reduce((acc, adicional) => {
      const tipo = adicional.tipo_adicional;
      if (!acc[tipo]) acc[tipo] = [];
      acc[tipo].push(adicional);
      return acc;
    }, {});
  }, [adicionales]);

  if (loading) {
    return (
      <SuiBox textAlign="center" py={3}>
        <SuiTypography variant="button" fontWeight="regular">
          Verificando información de adicionales...
        </SuiTypography>
      </SuiBox>
    );
  }

  if (error) {
    return (
      <SuiBox textAlign="center" py={3}>
        <SuiTypography variant="button" color="error" fontWeight="regular">
          {error}
        </SuiTypography>
      </SuiBox>
    );
  }

  if (
    !procesoData ||
    (!procesoData.procesos_judiciales && !procesoData.personas_a_cargo && !procesoData.sociedades)
  ) {
    return (
      <SuiBox p={2}>
        <SuiBox textAlign="center" py={3}>
          <SuiTypography variant="button" color="text" fontWeight="regular">
            El proceso no posee adicionales registrados.
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    );
  }

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case "PERSONA_CARGO":
        return "PERSONAS A CARGO";
      case "SOCIEDADES_PROCESO":
        return "SOCIEDADES CONYUGAL y/o PATRIMONIAL";
      case "PROCESO_JUDICIAL":
        return "PROCESOS JUDICIALES";
      default:
        return tipo;
    }
  };

  return (
    <SuiBox>
      <SuiBox display="flex" justifyContent="space-between" alignItems="center">
        <SuiButton
          variant="gradient"
          buttonColor="info"
          onClick={() => setShowNewAdicionalModal(true)}
        >
          Agregar Adicional
        </SuiButton>
      </SuiBox>
      {adicionales.length === 0 ? (
        <SuiBox textAlign="center" py={3}>
          <SuiTypography variant="button" color="text" fontWeight="regular">
            No hay adicionales registrados. Haga clic en &quot;Agregar Adicional&quot; para
            comenzar.
          </SuiTypography>
        </SuiBox>
      ) : (
        TIPOS_ADICIONALES.map((tipo) => {
          const adicionalesTipo = adicionalesPorTipo[tipo] || [];
          if (adicionalesTipo.length === 0) return null;

          return (
            <SuiBox key={tipo} pb={1}>
              <SuiBox pb={2}>
                <Divider sx={{ opacity: 0.8 }}>
                  <SuiTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="uppercase"
                    textColor="info"
                  >
                    {getTipoLabel(tipo)}
                  </SuiTypography>
                </Divider>
              </SuiBox>
              <Box
                sx={{
                  overflowX: "auto",
                  pb: 2,
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#555",
                    },
                  },
                }}
              >
                <Box sx={{ display: "flex", minWidth: "min-content" }}>
                  {adicionalesTipo.map((adicional) => (
                    <Box key={adicional.id} sx={{ minWidth: "350px" }}>
                      <AdicionalCard
                        adicional={adicional}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </SuiBox>
          );
        })
      )}

      {selectedAdicional && (
        <EditAdicionalModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedAdicional(null);
          }}
          onSubmit={handleEditSubmit}
          adicional={selectedAdicional}
          procesoData={procesoData}
        />
      )}

      <NuevoAdicionalForm
        open={showNewAdicionalModal}
        onClose={() => setShowNewAdicionalModal(false)}
        onSubmit={handleNewAdicionalSubmit}
        procesoId={procesoId}
        procesoData={procesoData} // pasar la procesoData aquí
      />
    </SuiBox>
  );
};

AdicionalesManager.propTypes = {
  procesoId: PropTypes.number.isRequired,
  showNewAdicionalModal: PropTypes.bool.isRequired,
  setShowNewAdicionalModal: PropTypes.func.isRequired,
  handleNext: PropTypes.func,
};

export default AdicionalesManager;
